// Configuration.
import { fields } from '../config';

// Utils.
import { taxes, between, findArrayObjByValue, futureValue } from './';

//------------------------------------------------------------------------------
// interest
//------------------------------------------------------------------------------

/**
 * Gets the annual interest rate from tax data.
 * @type {Number}
 */
export const getInterestRate = taxes.interestRate || 0;

//------------------------------------------------------------------------------
// deferrals - 401(k)
//------------------------------------------------------------------------------

/**
 * Gets the DESIRED 401(k) contribution amount taken out of an annual salary.
 * @param  {Number} salary      Annual salary.
 * @param  {Number} [deferrals] Contribution percentage.
 * @return {Number}             Desired contribution amount.
 */
export const getDeferralDesired = (salary, deferrals = 0) =>
  salary * (deferrals / 100);

/**
 * Gets the MAXIMUM amount of deferrable wages per year.
 * @type {Number}
 */
export const getDeferralLimit = taxes.deferralLimit || 0;

/**
 * Gets the ALLOWED 401(k) contribution amount taken out of an annual salary.
 * @param  {Number} deferralDesired Desired contribution amount.
 * @param  {Number} deferralLimit   Contribution limit.
 * @return {Number}                 Contribution amount allowed.
 */
export const getDeferralAllowed = (deferralDesired, deferralLimit) =>
  Math.min(deferralDesired, deferralLimit);

//------------------------------------------------------------------------------
// allowances - exemptions
//------------------------------------------------------------------------------

/**
 * Gets the SINGLE tax-exempt allowance claimable each pay period.
 * @param  {String} period Pay period, choose from: daily, weekly, biweekly,
 *                         semimonthly, monthly, quarterly, semiannual, annual.
 * @param  {Array}  [arr]  Data to search upon.
 * @return {Number}        Single allowance amount.
 */
export const getAllowanceLimit = (period, arr = taxes.payPeriods) =>
  findArrayObjByValue(arr, 'uid', period).allowanceLimit;

/**
 * Gets the TOTAL tax-exempt allowances claimable each pay period.
 * @param  {Number} allowanceLimit Single allowance amount.
 * @param  {Number} [exemptions]   Number of exemptions claimed.
 * @return {Number}                Total allowances claimed.
 */
export const getAllowanceTotal = (allowanceLimit, exemptions = 0) =>
  allowanceLimit * exemptions;

//------------------------------------------------------------------------------
// pay
//------------------------------------------------------------------------------

/**
 * Gets the number of payroll occurances each year.
 * @param  {String} period Pay period, choose from: daily, weekly, biweekly,
 *                         semimonthly, monthly, quarterly, semiannual, annual.
 * @param  {Array}  [arr]  Data to search upon.
 * @return {Number}        Annual occurances count.
 */
export const getPayOccurances = (period, arr = fields) => {
  const periodOptions = findArrayObjByValue(arr, 'uid', 'period').options;
  return findArrayObjByValue(periodOptions, 'uid', period).occurrences;
};

/**
 * Gets the GROSS payroll amount each pay period.
 * @param  {Number} salary        Annual salary.
 * @param  {Number} payOccurances Payroll occurances per year.
 * @return {Number}               Gross payroll amount.
 */
export const getPayGross = (salary, payOccurances) => salary / payOccurances;

/**
 * Gets the TOTAL wage payment amount each pay period.
 * @param  {Number} payGross              Gross payroll amount.
 * @param  {Number} [contributionsOutput] 401(k) contribution amount.
 * @return {Number}                       Payroll amount after contribution.
 */
export const getPayWages = (payGross, contributionsOutput = 0) =>
  contributionsOutput > 0 ? payGross - contributionsOutput : payGross;

/**
 * Gets the TAXABLE payroll amount each pay period.
 * @param  {Number} payWages       Payroll amount after 401(k) contribution.
 * @param  {Number} allowanceTotal Total allowances claimed.
 * @return {Number}                Taxable payroll amount.
 */
export const getPayTaxable = (payWages, allowanceTotal) =>
  payWages < allowanceTotal ? 0 : payWages - allowanceTotal;

//------------------------------------------------------------------------------
// taxes
//------------------------------------------------------------------------------

/**
 * Gets a person's tax bracket via their pay period, filing status,
 * and taxable payroll amount.
 * @param  {String} period     Pay period, choose from: daily, weekly, biweekly,
 *                             semimonthly, monthly, quarterly, semiannual,
 *                             annual.
 * @param  {String} status     Filing status, choose from: single, married.
 * @param  {Number} payTaxable Taxable payroll amount.
 * @param  {Array}  [arr]      Data to search upon.
 * @return {Object}            Tax bracket data.
 */
export const getTaxBracket = (
  period,
  status,
  payTaxable,
  arr = taxes.payPeriods,
) => {
  const taxTables = findArrayObjByValue(arr, 'uid', period)[status];
  let taxBracket = {};
  for (let i = taxTables.length - 1; i >= 0; i -= 1) {
    const min = taxTables[i].over;
    const max = taxTables[i].under;
    const isWithinBracket = between(payTaxable, min, max);
    if (isWithinBracket) {
      taxBracket = taxTables[i];
    }
  }
  return taxBracket;
};

/**
 * Gets the amount of taxes withheld each pay period using the
 * 'Percentage Method of Withholding'.
 * @param  {Number} payTaxable        Taxable payroll amount.
 * @param  {Number} taxBracketMin     Lower withholding amount.
 * @param  {Number} taxBracketBase    Base withholding amount.
 * @param  {Number} taxBracketPercent Additional percentage over lower
 *                                    withholding amount.
 * @return {Number}                   Taxes withheld each pay period.
 */
export const getTaxWithheld = (
  payTaxable,
  taxBracketMin,
  taxBracketBase,
  taxBracketPercent,
) => (payTaxable - taxBracketMin) * (taxBracketPercent / 100) + taxBracketBase;

//------------------------------------------------------------------------------
// <Results />
//------------------------------------------------------------------------------

export const getContributionsOutput = (
  deferralAllowed = 0,
  payOccurances = 0,
) => deferralAllowed / payOccurances;

export const getSavingsOutput = (
  taxWithheld401kFalse = 0,
  taxWithheld401kTrue = 0,
) => taxWithheld401kFalse - taxWithheld401kTrue;

export const getReductionsOutput = (
  contributionsOutput = 0,
  savingsOutput = 0,
) => contributionsOutput - savingsOutput;

//------------------------------------------------------------------------------
// <Chart />
//------------------------------------------------------------------------------

/**
 * Gets the accumulation periods data (year-based intervals).
 * @type {Array}
 */
export const getAccumulationPeriods = taxes.accumulationPeriods || [0];

/**
 * Gets the accumulation period data amount.
 * @param  {Number} index Index of data array.
 * @param  {Array}  [arr] Data to search upon.
 * @return {Number}       Number of years.
 */
export const getAccumulationPeriod = (index, arr = taxes.accumulationPeriods) =>
  arr[index] || 0;

/**
 * Gets the amount of 401(k) growth over time with or without interest.
 * @param  {Number} paymentValue       Contribution amount each pay period.
 * @param  {Number} payOccurances      Payroll occurances per year.
 * @param  {Number} accumulationPeriod Number of years.
 * @param  {Number} interestRate       Interest rate.
 * @return {Number}                    Amount of 401(k) growth per accumulation
 *                                     period.
 */
export const getAccumulationOutput = (
  paymentValue,
  payOccurances,
  accumulationPeriod,
  interestRate,
) => {
  // Flat rate, no interest. (reductions & contributions)
  if (interestRate <= 0) {
    return paymentValue * payOccurances * accumulationPeriod;
  }
  // Compounding interest. (savings)
  const rate = interestRate / 100 / payOccurances;
  const nper = accumulationPeriod * payOccurances;
  const pmt = paymentValue * -1;
  const pv = 0; // Additional periodic payment.
  const type = 1; // 0 = end of period, 1 = beginning of period.
  const fv = futureValue(rate, nper, pmt, pv, type);
  return Math.abs(fv);
};

//------------------------------------------------------------------------------
// default export
//------------------------------------------------------------------------------

/**
 * Calculates the core numbers needed for <Results /> and <Chart /> components.
 * @param  {Number} salary     Annual salary.
 * @param  {String} period     Pay period, choose from: daily, weekly, biweekly,
 *                             semimonthly, monthly, quarterly, semiannual,
 *                             annual.
 * @param  {String} status     Filing status, choose from: single, married.
 * @param  {Number} exemptions Number of exemptions claimed.
 * @param  {Number} deferrals  Contribution percentage.
 * @return {Object}            Math results.
 */
const formulas = (salary, period, status, exemptions, deferrals) => {
  const payOccurances = getPayOccurances(period);
  const payAnnual = salary;
  const payGross = getPayGross(salary, payOccurances);

  // Calculate 'contributions'.
  const deferralDesired = getDeferralDesired(salary, deferrals);
  const deferralLimit = getDeferralLimit;
  const deferralAllowed = getDeferralAllowed(deferralDesired, deferralLimit);
  const contributionsOutput = getContributionsOutput(
    deferralAllowed,
    payOccurances,
  );

  // Calculate 'savings'.
  const allowanceLimit = getAllowanceLimit(period);
  const allowanceTotal = getAllowanceTotal(allowanceLimit, exemptions);
  // 401(k): false
  const payWages401kFalse = getPayWages(payGross);
  const payTaxable401kFalse = getPayTaxable(payWages401kFalse, allowanceTotal);
  const taxBracket401kFalse = getTaxBracket(
    period,
    status,
    payTaxable401kFalse,
  );
  const taxBracketMin401kFalse = taxBracket401kFalse.over;
  const taxBracketBase401kFalse = taxBracket401kFalse.base;
  const taxBracketPercent401kFalse = taxBracket401kFalse.percent;
  const taxWithheld401kFalse = getTaxWithheld(
    payTaxable401kFalse,
    taxBracketMin401kFalse,
    taxBracketBase401kFalse,
    taxBracketPercent401kFalse,
  );
  // 401(k): true
  const payWages401kTrue = getPayWages(payGross, contributionsOutput);
  const payTaxable401kTrue = getPayTaxable(payWages401kTrue, allowanceTotal);
  const taxBracket401kTrue = getTaxBracket(period, status, payTaxable401kTrue);
  const taxBracketMin401kTrue = taxBracket401kTrue.over;
  const taxBracketBase401kTrue = taxBracket401kTrue.base;
  const taxBracketPercent401kTrue = taxBracket401kTrue.percent;
  const taxWithheld401kTrue = getTaxWithheld(
    payTaxable401kTrue,
    taxBracketMin401kTrue,
    taxBracketBase401kTrue,
    taxBracketPercent401kTrue,
  );
  const savingsOutput = getSavingsOutput(
    taxWithheld401kFalse,
    taxWithheld401kTrue,
  );

  // Calculate 'reductions'.
  const reductionsOutput = getReductionsOutput(
    contributionsOutput,
    savingsOutput,
  );

  return {
    payOccurances,
    payAnnual,
    payGross,
    deferralDesired,
    deferralLimit,
    deferralAllowed,
    contributionsOutput,
    allowanceLimit,
    allowanceTotal,
    payWages401kFalse,
    payTaxable401kFalse,
    taxBracket401kFalse,
    taxBracketMin401kFalse,
    taxBracketBase401kFalse,
    taxBracketPercent401kFalse,
    taxWithheld401kFalse,
    payWages401kTrue,
    payTaxable401kTrue,
    taxBracket401kTrue,
    taxBracketMin401kTrue,
    taxBracketBase401kTrue,
    taxBracketPercent401kTrue,
    taxWithheld401kTrue,
    savingsOutput,
    reductionsOutput,
  };
};

export default formulas;



// WEBPACK FOOTER //
// ./src/utils/formulas.js